/**
 * Shared authentication profile and role helpers for Safety workflows.
 * Ensures consistent USER_DATA + JWT token payload resolution,
 * role normalization, and explicit user_type detection.
 */

export const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

export const CHECKER_WORKFLOW_ROLES = new Set([
  "CHECKER",
  "SAFETY_CHECKER",
  "QUALITY_ENGINEER",
]);

export const isCheckerWorkflowRole = (value) =>
  CHECKER_WORKFLOW_ROLES.has(normalizeRole(value));

export const decodeJwtPayload = (token) => {
  try {
    const cleanToken = String(token || "").replace(/^Bearer\s+/i, "");
    const parts = cleanToken.split(".");

    if (parts.length !== 3) {
      return {};
    }

    const normalizedPayload = parts[1].replace(/-/g, "+").replace(/_/g, "/");

    const paddedPayload =
      normalizedPayload + "=".repeat((4 - (normalizedPayload.length % 4)) % 4);

    const decoded = decodeURIComponent(
      window
        .atob(paddedPayload)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );

    return JSON.parse(decoded);
  } catch (error) {
    console.error("Unable to decode access token:", error);
    return {};
  }
};

export const resolveAccessToken = () =>
  localStorage.getItem("ACCESS_TOKEN") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("access") ||
  localStorage.getItem("accessToken") ||
  localStorage.getItem("token") ||
  localStorage.getItem("TOKEN") ||
  "";

export const readStoredUserData = () => {
  try {
    const raw = localStorage.getItem("USER_DATA");

    if (!raw || raw === "undefined" || raw === "null") {
      return {};
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("Unable to parse USER_DATA:", error);
    return {};
  }
};

export const resolveSafetyUserProfile = () => {
  const storedProfile = readStoredUserData();
  const tokenProfile = decodeJwtPayload(resolveAccessToken());

  /*
   * USER_DATA may contain richer frontend fields, while the JWT contains
   * the authoritative role, Safety Officer, user type and access data.
   *
   * Token values take priority when available.
   */
  return {
    ...storedProfile,
    ...tokenProfile,
    roles: tokenProfile?.roles || storedProfile?.roles || [],
    accesses: tokenProfile?.accesses || storedProfile?.accesses || [],
  };
};

export const extractFirstRole = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) {
    return "";
  }

  const firstRole = roles[0];

  if (typeof firstRole === "string") {
    return firstRole;
  }

  return firstRole?.role || firstRole?.name || firstRole?.role_name || "";
};

export const resolveSafetyActiveRole = (profile = null, projectId = null) => {
  const storedRole =
    localStorage.getItem("FLOW_ROLE") ||
    localStorage.getItem("ACTIVE_ROLE") ||
    localStorage.getItem("ROLE") ||
    "";

  if (storedRole) {
    return normalizeRole(storedRole);
  }

  const userProfile = profile || resolveSafetyUserProfile();

  if (projectId) {
    const accesses = Array.isArray(userProfile?.accesses)
      ? userProfile.accesses
      : [];
    const matchingAccess = accesses.find(
      (access) =>
        Number(
          access?.project_id || access?.project?.id || access?.project,
        ) === Number(projectId),
    );

    const projectRole =
      matchingAccess?.active_role ||
      matchingAccess?.role ||
      matchingAccess?.role_name ||
      extractFirstRole(matchingAccess?.roles);

    if (projectRole) {
      return normalizeRole(projectRole);
    }
  }

  return normalizeRole(
    userProfile?.active_role ||
      userProfile?.role ||
      extractFirstRole(userProfile?.roles),
  );
};

/**
 * Dynamically resolves the authoritative organization ID for the safety workflow.
 *
 * Priority:
 * 1. Explicit project object organization (e.g. from getAssignedProjects)
 * 2. Matching access for the project in user's accesses
 * 3. Authenticated user profile / JWT token (profile.org, profile.org_id, profile.organization_id)
 * 4. Fallback: stored localStorage keys (only if neither project nor user profile specifies an org)
 */
export const resolveSafetyOrganizationId = (
  profile = null,
  projectId = null,
  activeAccess = null,
  project = null,
) => {
  const userProfile = profile || resolveSafetyUserProfile();

  // 1. Direct project object
  if (project && typeof project === "object") {
    const projectOrg =
      project.organization_id ??
      project.organization ??
      project.org_id ??
      project.org ??
      null;
    if (projectOrg != null && String(projectOrg).trim() !== "") {
      return String(projectOrg).trim();
    }
  }

  // 2. Matching access for the project
  let access = activeAccess;
  if (!access && projectId) {
    const accesses = Array.isArray(userProfile?.accesses)
      ? userProfile.accesses
      : [];
    access = accesses.find(
      (a) =>
        Number(a?.project_id || a?.project?.id || a?.project) ===
        Number(projectId),
    );
  }

  if (access && typeof access === "object") {
    const accessOrg =
      access.organization_id ??
      access.org_id ??
      access.org ??
      access.project?.organization_id ??
      access.project?.organization ??
      access.project?.org_id ??
      null;
    if (accessOrg != null && String(accessOrg).trim() !== "") {
      return String(accessOrg).trim();
    }
  }

  // 3. Authenticated user profile / JWT token
  const profileOrg =
    userProfile?.org ??
    userProfile?.org_id ??
    userProfile?.organization_id ??
    userProfile?.organization?.id ??
    userProfile?.orgId ??
    null;

  if (profileOrg != null && String(profileOrg).trim() !== "") {
    return String(profileOrg).trim();
  }

  // 4. Stored fallback if neither project nor user profile specifies an org
  const storedOrg =
    localStorage.getItem("ACTIVE_ORG_ID") ||
    localStorage.getItem("ORG_ID") ||
    localStorage.getItem("org_id") ||
    localStorage.getItem("organization_id") ||
    "";

  return String(storedOrg).trim();
};

